import { z } from 'zod'

import { applyAction, type Outcome, type PlaySummary, type Player } from '../engine.js'
import type { Rng } from '../rng.js'
import { READING_BANDS } from '../words.js'
import {
  hangmanEngine,
  skipTurn as skipHangmanTurn,
  toView as toHangmanView,
  type HangmanState,
  type HangmanView,
} from './hangman.js'
import {
  endRace,
  mathsRaceEngine,
  RACE_LEVELS,
  toMathsRaceView,
  type MathsRaceState,
  type MathsRaceView,
} from './maths-race.js'
import {
  isMemorySize,
  memoryEngine,
  settleReveal,
  skipMemoryTurn,
  toMemoryView,
  type MemorySize,
  type MemoryState,
  type MemoryView,
} from './memory.js'

/**
 * A round of whichever game a room is running.
 *
 * A tagged union rather than an engine held behind a wildcard type. Rooms only
 * ever run a handful of games, and this way the compiler checks every place
 * that handles one handles them all — adding a game breaks the build in exactly
 * the places that need changing, rather than failing quietly at run time on the
 * one path nobody tested.
 */
export type RoomRound =
  | { readonly key: 'hangman'; readonly state: HangmanState }
  | { readonly key: 'maths'; readonly state: MathsRaceState }
  | { readonly key: 'memory'; readonly state: MemoryState }

export type RoomRoundKey = RoomRound['key']

export type RoomRoundView =
  | { readonly key: 'hangman'; readonly view: HangmanView }
  | { readonly key: 'maths'; readonly view: MathsRaceView }
  | { readonly key: 'memory'; readonly view: MemoryView }

/**
 * Whether a game passes a go around, or everybody plays at once.
 *
 * The room needs this because turn machinery is meaningless in a race: there is
 * no turn to expire, and skipping past an absent player would be skipping past
 * somebody who is not holding anybody up.
 */
export type Pacing = 'turns' | 'simultaneous'

export interface GameRules {
  readonly pacing: Pacing
}

/**
 * There used to be a second rule here: whether a game's score was shared by the
 * room or worked out per player. There are no scores now, so every game in a
 * room is recorded the same way — who played, what they played, and how it went
 * — and the question does not arise.
 */
export const ROUND_RULES: Readonly<Record<RoomRoundKey, GameRules>> = {
  hangman: { pacing: 'turns' },
  maths: { pacing: 'simultaneous' },
  memory: { pacing: 'turns' },
}

/** What the host chose when starting the round. */
export const roundOptions = z.discriminatedUnion('game', [
  z.object({ game: z.literal('hangman'), band: z.enum(READING_BANDS) }),
  z.object({ game: z.literal('maths'), level: z.enum(RACE_LEVELS) }),
  z.object({
    game: z.literal('memory'),
    // Checked against the list rather than spelled out again, so adding a board
    // size is one line in one place.
    size: z.custom<MemorySize>(isMemorySize, 'Not a board size'),
  }),
])

export type RoundOptions = z.infer<typeof roundOptions>

export function createRound(
  options: RoundOptions,
  players: readonly Player[],
  rng: Rng,
  startedAt: number,
): RoomRound {
  if (options.game === 'hangman') {
    return {
      key: 'hangman',
      state: hangmanEngine.createInitialState({ band: options.band }, players, rng),
    }
  }

  if (options.game === 'maths') {
    return {
      key: 'maths',
      state: mathsRaceEngine.createInitialState({ level: options.level, startedAt }, players, rng),
    }
  }

  return {
    key: 'memory',
    state: memoryEngine.createInitialState({ size: options.size, startedAt }, players, rng),
  }
}

/**
 * Applies an untrusted action to whichever game is running.
 *
 * Routed through applyAction in every branch, so validation cannot be skipped
 * for one game by accident — which is exactly the kind of gap a per-game
 * `if` ladder grows over time.
 */
export function applyRoundAction(
  round: RoomRound,
  rawAction: unknown,
  context: { readonly playerId: string; readonly now: number },
): Outcome<RoomRound> {
  if (round.key === 'hangman') {
    const outcome = applyAction(hangmanEngine, round.state, rawAction, context)
    return outcome.ok
      ? { ok: true, state: { key: 'hangman', state: outcome.state } }
      : { ok: false, reason: outcome.reason, state: round }
  }

  if (round.key === 'maths') {
    const outcome = applyAction(mathsRaceEngine, round.state, rawAction, context)
    return outcome.ok
      ? { ok: true, state: { key: 'maths', state: outcome.state } }
      : { ok: false, reason: outcome.reason, state: round }
  }

  const outcome = applyAction(memoryEngine, round.state, rawAction, context)
  return outcome.ok
    ? { ok: true, state: { key: 'memory', state: outcome.state } }
    : { ok: false, reason: outcome.reason, state: round }
}

export function isRoundOver(round: RoomRound): boolean {
  if (round.key === 'hangman') return hangmanEngine.isTerminal(round.state)
  if (round.key === 'maths') return mathsRaceEngine.isTerminal(round.state)
  return memoryEngine.isTerminal(round.state)
}

export function summariseRound(round: RoomRound, endedAt: number): PlaySummary {
  if (round.key === 'hangman') return hangmanEngine.summarise(round.state, endedAt)
  if (round.key === 'maths') return mathsRaceEngine.summarise(round.state, endedAt)
  return memoryEngine.summarise(round.state, endedAt)
}

/** The roster the round was dealt to. Somebody who joined later is not in it. */
export function roundPlayerIds(round: RoomRound): readonly string[] {
  return round.state.turn.players.map((player) => player.id)
}

export function currentPlayerId(round: RoomRound): string | null {
  return round.state.turn.currentPlayerId
}

/**
 * Passes the turn without anybody having played.
 *
 * Returns the round unchanged for a game where nobody is waiting for a turn,
 * so a caller cannot accidentally "skip" a race and make it look as though
 * something happened.
 */
export function skipRoundTurn(round: RoomRound, now: number): RoomRound {
  if (round.key === 'hangman') return { key: 'hangman', state: skipHangmanTurn(round.state) }
  // Memory needs the clock: a pair that is still inside its moment must stay up
  // even though the go is moving on, or a phone locking at the wrong second
  // takes the look away from everybody else in the room.
  if (round.key === 'memory') return { key: 'memory', state: skipMemoryTurn(round.state, now) }
  return round
}

/**
 * Lets a round move on without anybody having played.
 *
 * Memory holds a mismatched pair face up for a moment, and that moment ends by
 * the clock rather than by anybody doing something. Without this the room's own
 * sweep has nothing to call, and two cards stay showing on the television until
 * the next player happens to tap — which is a free look nobody earned.
 *
 * Returns the round unchanged for any game with nothing to settle, so a caller
 * cannot make it look as though something happened.
 */
export function settleRound(round: RoomRound, now: number): RoomRound {
  if (round.key !== 'memory') return round

  const settled = settleReveal(round.state, now)
  return settled === round.state ? round : { key: 'memory', state: settled }
}

/** Ends a round early — out of time, or everybody still here has finished. */
export function endRound(round: RoomRound): RoomRound {
  if (round.key !== 'maths') return round
  return { key: 'maths', state: endRace(round.state) }
}

/**
 * What one client is allowed to see.
 *
 * `forPlayerId` is the recipient. Hangman looks the same to everybody, but a
 * race has to tell each player her own score without telling the room, so the
 * view is built per client rather than broadcast once.
 *
 * Built by the engine that owns the state, so the word or the answer is
 * stripped by the same code that put it there.
 */
export function roundView(round: RoomRound, forPlayerId: string | null): RoomRoundView {
  if (round.key === 'hangman') return { key: 'hangman', view: toHangmanView(round.state) }
  if (round.key === 'maths') {
    return { key: 'maths', view: toMathsRaceView(round.state, forPlayerId) }
  }

  // Memory looks the same to everybody: what is face up is face up, and the
  // rest is nobody's to see, including the player whose go it is.
  return { key: 'memory', view: toMemoryView(round.state) }
}
